import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles, FileText, Copy, RotateCcw, ChevronDown, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { fetchCallAi, retryCallAi } from "@/lib/api";
import { formatClock } from "@/lib/format";

const MOTIF_LABELS = {
  "crevaison": "Crevaison",
  "remplacement": "Remplacement",
  "devis": "Devis",
  "prise de rendez-vous": "Prise de rendez-vous",
  "suivi d'intervention": "Suivi d'intervention",
  "réclamation": "Réclamation",
  "autre": "Autre",
};

// Labelled chips for the filled summary fields (null/empty fields hidden).
const summaryChips = (s) => {
  if (!s) return [];
  const chips = [];
  if (s.motif) chips.push({ label: "Motif", value: MOTIF_LABELS[s.motif] || s.motif });
  if (s.vehicule) chips.push({ label: "Véhicule", value: s.vehicule });
  const dims = Array.isArray(s.dimensions_pneus) ? s.dimensions_pneus.filter(Boolean) : [];
  let pneus = dims.join(", ");
  if (s.nombre_pneus) pneus = `${pneus ? pneus + " — " : ""}×${s.nombre_pneus}`;
  if (pneus) chips.push({ label: "Pneus", value: pneus });
  if (s.lieu_intervention) chips.push({ label: "Lieu", value: s.lieu_intervention });
  if (s.creneau_souhaite) chips.push({ label: "Créneau", value: s.creneau_souhaite });
  if (s.urgence === "oui") chips.push({ label: "Urgence", value: "Immobilisé" });
  if (s.urgence === "non") chips.push({ label: "Urgence", value: "Pas urgent" });
  if (s.prix_evoque) chips.push({ label: "Prix", value: s.prix_evoque });
  if (s.prochaine_action) chips.push({ label: "Prochaine action", value: s.prochaine_action });
  return chips;
};

const copyText = (s) => {
  const lines = [s?.resume || ""];
  for (const c of summaryChips(s)) lines.push(`${c.label} : ${c.value}`);
  return lines.filter(Boolean).join("\n");
};

const speakerLabel = (segment, speakers) => {
  const mapped = speakers?.[segment.speaker];
  return mapped || "Inconnu";
};

// AI section of an expanded call: summary card + collapsible transcript.
// States: none / pending / processing / failed / done / 403 (not your call).
export const CallAiSection = ({ callId, hasRecording, onSeek }) => {
  const qc = useQueryClient();
  const [showTranscript, setShowTranscript] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["call-ai", callId],
    queryFn: () => fetchCallAi(callId),
    refetchInterval: (q) =>
      ["pending", "processing"].includes(q.state.data?.status) ? 15000 : false,
  });
  const retryMut = useMutation({
    mutationFn: () => retryCallAi(callId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["call-ai", callId] });
      toast.success("Transcription relancée");
    },
    onError: () => toast.error("Impossible de relancer la transcription"),
  });

  if (!hasRecording) {
    return (
      <div data-testid="ai-none" className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-sm text-slate-400 italic">Pas d'enregistrement</p>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-400">Chargement…</div>
    );
  }
  if (error?.response?.status === 403) {
    return (
      <div data-testid="ai-forbidden" className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-2 text-sm text-slate-400">
        <Lock className="h-4 w-4" /> Transcription réservée à l'agent qui a traité cet appel.
      </div>
    );
  }
  if (!data || data.status === "none") {
    return (
      <div data-testid="ai-none" className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-sm text-slate-400 italic">Pas d'enregistrement</p>
      </div>
    );
  }
  if (data.status === "pending" || data.status === "processing") {
    return (
      <div data-testid="ai-processing" className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 flex items-center gap-2.5 text-sm text-emerald-800">
        <Loader2 className="h-4 w-4 animate-spin" /> Transcription en cours…
      </div>
    );
  }
  if (data.status === "failed") {
    return (
      <div data-testid="ai-failed" className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-center justify-between gap-3">
        <p className="text-sm text-red-700">Échec de la transcription{data.error ? ` — ${data.error}` : ""}</p>
        <button
          type="button"
          data-testid="ai-retry-btn"
          onClick={() => retryMut.mutate()}
          disabled={retryMut.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white text-red-700 text-xs font-semibold px-3 py-1.5 hover:bg-red-50 disabled:opacity-50 whitespace-nowrap"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Réessayer
        </button>
      </div>
    );
  }

  const summary = data.summary;
  const segments = data.segments || [];
  const chips = summaryChips(summary);

  return (
    <>
      <div data-testid="ai-summary-card" className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2">
            <span className="h-6 w-6 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><Sparkles className="h-3.5 w-3.5" /></span>
            <p className="font-heading font-semibold text-slate-800 text-sm">Résumé de l'IA</p>
          </div>
          <button
            type="button"
            data-testid="copy-summary-btn"
            onClick={() => {
              navigator.clipboard.writeText(copyText(summary)).then(
                () => toast.success("Résumé copié"),
                () => toast.error("Copie impossible"),
              );
            }}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-700 transition-colors whitespace-nowrap"
          >
            <Copy className="h-3.5 w-3.5" /> Copier
          </button>
        </div>
        {summary?.resume && <p data-testid="ai-resume" className="text-sm text-slate-700 leading-relaxed">{summary.resume}</p>}
        {chips.length > 0 && (
          <div data-testid="ai-chips" className="flex flex-wrap gap-1.5 mt-3">
            {chips.map((c) => (
              <span key={c.label} className="rounded-full bg-slate-100 border border-slate-200 px-2.5 py-1 text-xs text-slate-700">
                <span className="font-semibold text-slate-500">{c.label}</span> · {c.value}
              </span>
            ))}
          </div>
        )}
        <p className="text-[11px] text-slate-400 italic mt-3">Généré par IA — peut contenir des erreurs</p>
      </div>

      <div data-testid="transcription-card" className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <button
          type="button"
          data-testid="transcription-toggle"
          onClick={() => setShowTranscript(!showTranscript)}
          aria-expanded={showTranscript}
          className="w-full flex items-center justify-between gap-2 p-4 text-left hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="h-6 w-6 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"><FileText className="h-3.5 w-3.5" /></span>
            <p className="font-heading font-semibold text-slate-800 text-sm">Transcription</p>
            <span className="text-xs text-slate-400">({segments.length} segment{segments.length > 1 ? "s" : ""})</span>
          </div>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${showTranscript ? "rotate-180" : ""}`} />
        </button>
        {showTranscript && (
          <div data-testid="transcription-segments" className="border-t border-slate-100 p-4 space-y-3">
            {segments.map((seg, i) => {
              const label = speakerLabel(seg, summary?.speakers);
              return (
                <div key={i} className="flex gap-2.5 items-start">
                  <div className="flex flex-col items-start gap-1 shrink-0 w-24">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      label === "Client"
                        ? "bg-blue-100 text-blue-800"
                        : label && label !== "locuteur"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-slate-100 text-slate-500"
                    }`}>
                      {label}
                    </span>
                    {seg.start != null && (
                      <button
                        type="button"
                        data-testid={`segment-seek-${i}`}
                        onClick={() => onSeek?.(seg.start)}
                        className="text-[11px] text-slate-400 tabular-nums hover:text-emerald-700 hover:underline"
                      >
                        {formatClock(seg.start)}
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed min-w-0">{seg.text}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};
